/**
 * ContextStripHost — the add-in's host for the SHARED `EmailContextStrip`
 * (story pinned-pane, AC-4..AC-7).
 *
 * A HOST, not a component: the pill copy, the closed state set and the single
 * action per state all come from `@product-app/common/workflows/**` (design
 * Constraint 20). There is no forked strip under this tree. What lives here is
 * the WIRING — which existing surface each action opens.
 *
 * ROUTING, and what each action must NOT do:
 *   Run analysis            → the EXISTING analyze path (App's handleProcess).
 *   Review & Approve        → the EXISTING pre-launch drawer, pre-filled.
 *   Open the run /
 *   Open the decision /
 *   Open the one that
 *     needs you             → the EXISTING InstanceDetailDrawer for
 *                             `primary_run_id`. No second decision surface is
 *                             built here (design Constraint 17); the drawer is
 *                             `running-drawer` / `shared-decision-components`'
 *                             territory.
 *   Relaunch                → OPENS THE LAUNCH FORM AND RETURNS. It never
 *                             creates a run, never mutates or resumes the
 *                             prior run and never references it as a parent
 *                             (operator decision 2026-08-25: a one-click
 *                             re-run would bypass every pre-dispatch guard
 *                             `prefill-drawer` adds).
 *   Start another workflow  → dropdown of the catalog + arrow; opens the
 *                             same form for the picked workflow so a wrong
 *                             recommendation is never a dead end.
 *
 * THE GUARD IS NOT PRE-EMPTED HERE. This host runs no client-side
 * concurrent-run check, and it renders no 409 of its own: AC-7 is satisfied by
 * the launch form's EXISTING refusal renderer in `InstanceDetailDrawer`
 * (locked by `InstanceDetailDrawer.concurrentRun.test.tsx`). Asserting a
 * refusal render here would test a path this story does not own.
 *
 * The identity travels the way it already does — on the drawer's
 * `internetMessageId` prop, the declared contract field. No NEW launch-payload
 * field is introduced (design Constraint 23).
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { EmailContextStrip } from "@product-app/common/workflows/components/EmailContextStrip";
import type { WorkflowOption } from "@product-app/common/workflows/components/EmailContextStrip";
import { deriveEmailContext } from "@product-app/common/workflows/emailContextState";
import type { EmailContextPrimaryAction } from "@product-app/common/workflows/emailContextState";
import {
  canonicalRecommendedWorkflowId,
  findRecommendedCatalogEntry,
} from "@product-app/common/workflows/launch/launchGuards";
import { isLaunchableInPlugin } from "../features/pluginCatalogExclusions";
import type {
  EmailRunItem,
  EmailRunStateResponse,
  WorkflowCatalogEntry,
} from "../types/workflow";
import type { EmailContext as MailboxEmailContext } from "../hooks/useMailboxItem";
import { useEmailContextState } from "../hooks/useEmailContextState";
import { orchestratorApi } from "../services/orchestratorApi";
import { resolveTriageMailbox } from "../features/sharedMailbox/triageMailbox";
import { workflowLabel } from "./CatalogCard";

/**
 * The ONE re-run refusal log line on this side of the wire (Monitoring DoD).
 *
 * It carries the greppable token, the BLOCKING instance id and the guard's own
 * reason code — and nothing else. Never the subject, the body, the sender or
 * any extraction content. The instance id is the join key to the guard's
 * server-side refusal log ("concurrent-run guard REFUSED … blocking_instance_id
 * =…"), which is the join the DoD mapping claims; without this line that claim
 * would be unimplemented by any numbered deliverable.
 */
export function logRerunRefused(refusal: {
  instance_id: string;
  reason_code: string;
}): void {
  console.warn("[pinned-pane] rerun_refused", {
    instance_id: refusal.instance_id,
    reason_code: refusal.reason_code,
  });
}

/** The copy shown when the lookup itself failed. Not a seventh strip state —
 *  the strip stays in `resolving` and offers nothing, and this one sentence
 *  says why rather than leaving "Checking…" spinning forever. */
export const CONTEXT_LOOKUP_FAILED_COPY =
  "Couldn't check this email — try selecting it again.";

export interface ContextStripHostProps {
  /** The selected item, from `useMailboxItem`. */
  emailContext: MailboxEmailContext | null;
  /** The existing analyze-this-email path. */
  onRunAnalysis: () => void;
  /** The existing InstanceDetailDrawer live-mode open path. */
  onOpenRun: (instanceId: string, workflowId: string) => void;
  /**
   * The existing pre-launch drawer. `null` means "no workflow could be
   * resolved" — the host lands the AM on the launch catalog rather than
   * guessing one for them.
   */
  onOpenPreLaunch: (
    workflowId: string | null,
    briefIdentity?: {
      internetMessageId: string;
      mailboxAddress?: string | null;
      clientName?: string | null;
      clientSk?: number | null;
      clientCity?: string | null;
      clientState?: string | null;
      clientBranch?: string | null;
      policyNumber?: string | null;
      prefillValues?: Record<string, unknown> | null;
      fieldConfidence?: Record<string, "high" | "medium" | "low"> | null;
      triageOutcome?: string | null;
    },
  ) => void;
  /** AM picked a workflow from the catalog — identify client + prefill, no recommendation. */
  onStartSelectedWorkflow?: (workflowId: string) => void;
  /** Injected only so tests drive the strip without fetch. */
  getEmailRunState?: (
    messageIdentity: string,
    mailboxAddress?: string | null,
  ) => Promise<EmailRunStateResponse>;
  /** Injected so tests can pin the Start another workflow catalog. */
  getCatalog?: () => Promise<WorkflowCatalogEntry[]>;
  /** Shared catalog from the host (App already loads it). Skips a second fetch. */
  catalog?: WorkflowCatalogEntry[];
  /** Bump to re-read the selected email's state (App bumps it on launch). */
  refreshSignal?: number;
  /** Disables the lookup entirely (tests). */
  disabled?: boolean;
}

/** The run a "open the run / decision / the one that needs you" action opens. */
function primaryRun(lookup: EmailRunStateResponse | null): EmailRunItem | null {
  if (!lookup) return null;
  const runs = lookup.runs ?? [];
  const byId = lookup.primary_run_id
    ? runs.find((r) => r.instance_id === lookup.primary_run_id)
    : undefined;
  return byId ?? null;
}

/**
 * Which workflow the launch form should open on. The most recently updated run
 * on this email, else the prepared brief's recommendation, else nothing — and
 * "nothing" means the AM picks, never a guess.
 */
function launchWorkflowId(lookup: EmailRunStateResponse | null): string | null {
  if (!lookup) return null;
  const runs = [...(lookup.runs ?? [])].sort((a, b) =>
    String(b.updated_at ?? "").localeCompare(String(a.updated_at ?? "")),
  );
  return runs[0]?.workflow_id ?? lookup.brief?.recommended_workflow_id ?? null;
}

/** Client/prefill the analysis row already has — Open the brief, Relaunch,
 *  and Start another all open the SAME form and must seed it the same way.
 *  Relaunch used to pass workflow id only, so the tile stayed empty even
 *  when `email_analysis` had client_sk. */
function briefIdentityFromLookup(
  emailContext: MailboxEmailContext | null,
  lookup: EmailRunStateResponse | null,
): {
  internetMessageId: string;
  mailboxAddress?: string | null;
  clientName?: string | null;
  clientSk?: number | null;
  clientCity?: string | null;
  clientState?: string | null;
  clientBranch?: string | null;
  policyNumber?: string | null;
  prefillValues?: Record<string, unknown> | null;
  fieldConfidence?: Record<string, "high" | "medium" | "low"> | null;
  triageOutcome?: string | null;
} {
  const brief = lookup?.brief;
  return {
    internetMessageId: emailContext?.internetMessageId ?? "",
    mailboxAddress: brief?.mailbox_address ?? resolveTriageMailbox(emailContext ?? {}),
    clientName: brief?.client_name ?? null,
    clientSk: brief?.client_sk ?? null,
    clientCity: brief?.client_city ?? null,
    clientState: brief?.client_state ?? null,
    clientBranch: brief?.client_branch ?? null,
    policyNumber: brief?.policy_number ?? null,
    prefillValues: brief?.prefill_values ?? null,
    fieldConfidence: brief?.field_confidence ?? null,
    triageOutcome: brief?.triage_outcome ?? null,
  };
}

function catalogLabel(entry: WorkflowCatalogEntry): string {
  return (entry.name || "").trim() || workflowLabel(entry.ref || entry.id);
}

export function ContextStripHost({
  emailContext,
  onRunAnalysis,
  onOpenRun,
  onOpenPreLaunch,
  onStartSelectedWorkflow,
  getEmailRunState,
  getCatalog,
  catalog: catalogFromHost,
  refreshSignal,
  disabled = false,
}: ContextStripHostProps): React.ReactElement {
  const mailboxAddress = resolveTriageMailbox(emailContext ?? {});

  const lookupFn = useCallback(
    (messageIdentity: string, mailbox?: string | null) =>
      (getEmailRunState ?? orchestratorApi.getEmailRunState)(messageIdentity, mailbox),
    [getEmailRunState],
  );

  const [fetchedCatalog, setFetchedCatalog] = useState<WorkflowCatalogEntry[]>([]);
  useEffect(() => {
    if (disabled || catalogFromHost !== undefined) return;
    let cancelled = false;
    const load = getCatalog ?? (() => orchestratorApi.getCatalog());
    void load()
      .then((entries) => {
        if (!cancelled) setFetchedCatalog(Array.isArray(entries) ? entries : []);
      })
      .catch(() => {
        if (!cancelled) setFetchedCatalog([]);
      });
    return () => {
      cancelled = true;
    };
  }, [catalogFromHost, disabled, getCatalog]);
  const catalog = catalogFromHost ?? fetchedCatalog;

  const { noItem, lookup, error } = useEmailContextState({
    emailContext,
    getEmailRunState: lookupFn,
    mailboxAddress,
    refreshSignal,
    disabled,
  });

  // The shared derivation owns the state, the pill and the action. This host
  // derives nothing — one copy table, one consumer per host.
  const context = deriveEmailContext(lookup, { noItem });

  const handlePrimary = useCallback(
    (action: EmailContextPrimaryAction) => {
      switch (action.kind) {
        case "run_analysis":
          onRunAnalysis();
          return;
        case "open_brief":
          onOpenPreLaunch(
            lookup?.brief?.recommended_workflow_id ?? null,
            briefIdentityFromLookup(emailContext, lookup),
          );
          return;
        case "open_run":
        case "open_decision":
        case "open_needs_you": {
          const run = primaryRun(lookup);
          if (!run) return;
          onOpenRun(run.instance_id, run.workflow_id);
          return;
        }
        case "relaunch":
          // Opens the form. Does not launch. Seeds client/prefill from the
          // stored brief the same way Open the brief does — otherwise the
          // tile stays empty and only Re-analyse (a full recompute) fills it.
          onOpenPreLaunch(
            launchWorkflowId(lookup),
            briefIdentityFromLookup(emailContext, lookup),
          );
          return;
        default:
          return;
      }
    },
    [emailContext, lookup, onOpenPreLaunch, onOpenRun, onRunAnalysis],
  );

  const handleSecondary = useCallback(() => {
    // "Launch another" — the same form, no run created, nothing copied from
    // the existing run beyond which workflow it was. The EMAIL's brief
    // (client / prefill) still belongs on the form.
    onOpenPreLaunch(
      launchWorkflowId(lookup),
      briefIdentityFromLookup(emailContext, lookup),
    );
  }, [emailContext, lookup, onOpenPreLaunch]);

  const workflowOptions: WorkflowOption[] = useMemo(
    () =>
      catalog
        .filter((entry) => !entry.hidden && entry.id !== "analyze_email" && isLaunchableInPlugin(entry))
        .map((entry) => ({ id: entry.id, label: catalogLabel(entry) })),
    [catalog],
  );

  const recommendedWorkflowLabel = useMemo(() => {
    const rec = canonicalRecommendedWorkflowId(
      lookup?.brief?.recommended_workflow_id ?? null,
    );
    if (!rec) return null;
    // An exact id wins; a legacy entry that merely remaps onto it only stands in.
    const match = findRecommendedCatalogEntry(catalog, rec);
    return match ? catalogLabel(match) : workflowLabel(rec);
  }, [catalog, lookup?.brief?.recommended_workflow_id]);

  return (
    <div className="px-4 pt-3" data-testid="context-strip-host">
      <EmailContextStrip
        key={emailContext?.internetMessageId ?? "none"}
        state={context.state}
        pill={context.pill}
        pillTone={context.pillTone}
        pillAriaLabel={context.pillAriaLabel}
        primaryAction={context.primaryAction}
        secondaryAction={context.secondaryAction}
        onPrimaryAction={handlePrimary}
        onSecondaryAction={handleSecondary}
        subject={emailContext?.subject}
        from={emailContext?.sender}
        recommendedWorkflowLabel={
          // Story 26741: `unmatched` shares `brief_ready`'s `open_brief`
          // action, and the strip DROPS an `open_brief` primary whenever this
          // prop is null (an unnameable workflow is a junk brief). Passing it
          // for both is what keeps "Add the missing details" on screen. When
          // an unmatched row genuinely has no recommendation — the common
          // case, since 0056 classifies a missing one AS unmatched — this
          // stays null and the catalog picker takes over, which is the right
          // fallback: the AM supplies the workflow as well as the policy.
          context.state === "brief_ready" || context.state === "unmatched"
            ? recommendedWorkflowLabel
            : null
        }
        workflowOptions={workflowOptions}
        onStartAnotherWorkflow={(workflowId) => {
          if (onStartSelectedWorkflow) onStartSelectedWorkflow(workflowId);
          else onOpenPreLaunch(workflowId, briefIdentityFromLookup(emailContext, lookup));
        }}
      />
      {error && (
        <p
          role="status"
          data-testid="context-strip-lookup-error"
          className="mt-1 text-xs text-primary-gray-light-500"
        >
          {CONTEXT_LOOKUP_FAILED_COPY}
        </p>
      )}
    </div>
  );
}

export default ContextStripHost;
